import { Info, Monitor, Palette, Settings, SortDesc, X } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { WithContext as ReactTags } from "react-tag-input";
import { THEME_MODES, useTheme } from "../ThemeContext";
import { VIEW_MODE } from "../constants";
import { useCodiffLinks as useCodiffLinksSetting } from "../hooks/useCodiffLinks";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { CollapsibleSection } from "./CollapsibleSection";
import { ThemeSelector } from "./ThemeSelector";

export const SettingsModal = ({
	isOpen,
	onClose,
	showDrafts,
	showBotPRs,
	onToggleDrafts,
	onToggleBotPRs,
	sortOption,
	onSetSortOption,
	showPendingWithProjectRules,
	onTogglePendingWithProjectRules,
	showPendingWithAllApprovals,
	onTogglePendingWithAllApprovals,
	greyOutBlockingComments,
	onToggleGreyOutBlockingComments,
	enableCommentsExpansion,
	onToggleCommentsExpansion,
	preloadResponses,
	onTogglePreloadResponses,
	quickFilterStyle,
	onSetQuickFilterStyle,
	viewMode,
	onSetViewMode,
	showSwimlaneMergedColumn,
	onToggleShowSwimlaneMergedColumn,
	showSwimlaneDeletedColumn,
	onToggleShowSwimlaneDeletedColumn,
	swimlaneWorkstreamsEnabled,
	onToggleSwimlaneWorkstreams,
	useLocalTime,
	onToggleUseLocalTime,
	watchProjects,
	setWatchProjects,
	excludeProjects,
	setExcludeProjects,
	swimlaneHideCreatedDaysThreshold,
	setSwimlaneHideCreatedDaysThreshold,
	swimlaneHideUpdatedDaysThreshold,
	setSwimlaneHideUpdatedDaysThreshold,
}) => {
	const [enableShortcut, setenableShortcut] = useLocalStorage(
		"enableShortcut",
		true,
	);
	const [enableSystemThemeControls, setEnableSystemThemeControls] =
		useLocalStorage("enableSystemThemeControls", true);
	const [previousSystemThemeMode, setPreviousSystemThemeMode] = useLocalStorage(
		"previousSystemThemeMode",
		THEME_MODES.SYSTEM,
	);
	const [useCodiffLinks, setUseCodiffLinks] = useCodiffLinksSetting();
	const { themeMode, resolvedThemeMode, setThemeMode } = useTheme();
	const modalRef = useRef(null);

	const toggleSystemThemeControls = () => {
		const nextValue = !enableSystemThemeControls;
		setEnableSystemThemeControls(nextValue);
		if (!nextValue) {
			setPreviousSystemThemeMode(themeMode);
			setThemeMode(resolvedThemeMode);
		} else {
			setThemeMode(previousSystemThemeMode);
		}
	};

	// Handle click outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (modalRef.current && !modalRef.current.contains(e.target)) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			// Lock scroll when modal is open
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			// Restore scroll when modal closes
			document.body.style.overflow = "auto";
		};
	}, [isOpen, onClose]);

	// Focus trap and scroll to top
	useEffect(() => {
		if (isOpen && modalRef.current) {
			// Scroll modal content to top
			const scrollableContent =
				modalRef.current.querySelector(".overflow-y-auto");
			if (scrollableContent) {
				scrollableContent.scrollTop = 0;
			}

			// Auto-focus first interactive element (for keyboard users)
			const focusableElements = modalRef.current.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			);

			if (focusableElements?.length) {
				focusableElements[0].focus();
			}
		}
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center z-50 animate-in fade-in duration-200"
			role="dialog"
			aria-modal="true"
			aria-labelledby="settings-title"
		>
			<div
				ref={modalRef}
				className="bg-[var(--color-bg-primary)] rounded-xl shadow-xl w-full max-w-md mx-4 p-6 animate-in zoom-in-95 duration-200"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex justify-between items-center mb-6">
					<h2
						id="settings-title"
						className="text-xl font-semibold text-[var(--color-text-primary)]"
					>
						Settings
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="cursor-pointer text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] p-1 rounded-full hover:bg-[var(--color-bg-secondary)] transition-colors"
						aria-label="Close settings"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
					<CollapsibleSection
						title="Display Options"
						icon={Monitor}
						iconColor="text-blue-500"
						storageKey="settings-display"
					>
						<div className="space-y-4">
							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showDrafts"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show draft merge requests
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showDrafts"
										checked={showDrafts}
										onChange={() => onToggleDrafts(!showDrafts)}
										className="sr-only"
										aria-checked={showDrafts}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											showDrafts
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() => onToggleDrafts(!showDrafts)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											showDrafts ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onToggleDrafts(!showDrafts)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showBotPRs"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show bot-created merge requests
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showBotPRs"
										checked={showBotPRs}
										onChange={() => onToggleBotPRs(!showBotPRs)}
										className="sr-only"
										aria-checked={showBotPRs}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											showBotPRs
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() => onToggleBotPRs(!showBotPRs)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											showBotPRs ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onToggleBotPRs(!showBotPRs)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="greyOutBlockingComments"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Grey out MRs with blocking comments
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="greyOutBlockingComments"
										checked={greyOutBlockingComments}
										onChange={() =>
											onToggleGreyOutBlockingComments(!greyOutBlockingComments)
										}
										className="sr-only"
										aria-checked={greyOutBlockingComments}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											greyOutBlockingComments
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() =>
											onToggleGreyOutBlockingComments(!greyOutBlockingComments)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											greyOutBlockingComments
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onToggleGreyOutBlockingComments(!greyOutBlockingComments)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="enableCommentsExpansion"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Enable comments expansion
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="enableCommentsExpansion"
										checked={enableCommentsExpansion}
										onChange={() =>
											onToggleCommentsExpansion(!enableCommentsExpansion)
										}
										className="sr-only"
										aria-checked={enableCommentsExpansion}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											enableCommentsExpansion
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() =>
											onToggleCommentsExpansion(!enableCommentsExpansion)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											enableCommentsExpansion
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onToggleCommentsExpansion(!enableCommentsExpansion)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between gap-4 py-2">
								<label
									htmlFor="preloadResponses"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Load Needs Response in the background
									<span className="block text-xs font-normal text-[var(--color-text-secondary)]">
										Shows the pending count in the View menu from any view
									</span>
								</label>
								<div className="relative inline-block w-12 shrink-0 align-middle select-none">
									<input
										type="checkbox"
										id="preloadResponses"
										checked={preloadResponses}
										onChange={() => onTogglePreloadResponses(!preloadResponses)}
										className="sr-only peer"
										aria-checked={preloadResponses}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent)] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--color-bg-primary)] ${
											preloadResponses
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() => onTogglePreloadResponses(!preloadResponses)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											preloadResponses ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onTogglePreloadResponses(!preloadResponses)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="useLocalTime"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Display times in local timezone
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="useLocalTime"
										checked={useLocalTime}
										onChange={() => onToggleUseLocalTime(!useLocalTime)}
										className="sr-only"
										aria-checked={useLocalTime}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											useLocalTime
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() => onToggleUseLocalTime(!useLocalTime)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											useLocalTime ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onToggleUseLocalTime(!useLocalTime)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="quickFilterStyle"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Labelled quick filters
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="quickFilterStyle"
										checked={quickFilterStyle === "pills"}
										onChange={() =>
											onSetQuickFilterStyle(
												quickFilterStyle === "pills" ? "icons" : "pills",
											)
										}
										className="sr-only"
										aria-checked={quickFilterStyle === "pills"}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											quickFilterStyle === "pills"
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() =>
											onSetQuickFilterStyle(
												quickFilterStyle === "pills" ? "icons" : "pills",
											)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											quickFilterStyle === "pills"
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onSetQuickFilterStyle(
												quickFilterStyle === "pills" ? "icons" : "pills",
											)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="py-2">
								<div className="flex flex-col space-y-3">
									<div className="flex items-center gap-2">
										<div
											className="text-sm font-medium text-[var(--color-text-primary)]"
											id="view-mode-label"
										>
											View mode
										</div>
										<div className="group">
											<Info className="w-4 h-4 text-[var(--color-text-secondary)] cursor-help" />
											<div
												className="fixed w-80 p-3 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto text-xs text-[var(--color-text-primary)] leading-relaxed ml-6 -mt-10"
												style={{ zIndex: 9999 }}
											>
												<p>
													<strong>Standard:</strong> Classic list view of MRs
													grouped by category.
												</p>
												<p className="mt-2">
													<strong>Swimlane:</strong> Drag and drop MRs between
													columns to organize your workflow.
												</p>
												<p className="mt-2">
													<strong>Review Board:</strong> Kanban-style board
													focused on review status with auto-categorization.
												</p>
												<p className="mt-2">
													<strong>By Repository:</strong> Open MRs grouped into
													one horizontal lane per repository.
												</p>
												<p className="mt-2">
													<strong>Needs Response:</strong> Your open MRs with
													unanswered comments or threads from people.
												</p>
												<p className="mt-2 text-[var(--color-text-secondary)]">
													Swimlane and Review Board changes are stored locally
													in your browser.
												</p>
											</div>
										</div>
									</div>
									<div
										className="flex flex-col space-y-2"
										role="radiogroup"
										aria-labelledby="view-mode-label"
									>
										<label className="inline-flex cursor-pointer items-center rounded-md px-1.5 py-1 hover:bg-[var(--color-bg-secondary)] transition-colors">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4 cursor-pointer"
												name="viewMode"
												value={VIEW_MODE.STANDARD}
												checked={viewMode === VIEW_MODE.STANDARD}
												onChange={() => onSetViewMode(VIEW_MODE.STANDARD)}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												Standard
											</span>
										</label>
										<label className="inline-flex cursor-pointer items-center rounded-md px-1.5 py-1 hover:bg-[var(--color-bg-secondary)] transition-colors">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4 cursor-pointer"
												name="viewMode"
												value={VIEW_MODE.SWIMLANE}
												checked={viewMode === VIEW_MODE.SWIMLANE}
												onChange={() => onSetViewMode(VIEW_MODE.SWIMLANE)}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												Swimlane
											</span>
										</label>
										<label className="inline-flex cursor-pointer items-center rounded-md px-1.5 py-1 hover:bg-[var(--color-bg-secondary)] transition-colors">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4 cursor-pointer"
												name="viewMode"
												value={VIEW_MODE.REVIEW_BOARD}
												checked={viewMode === VIEW_MODE.REVIEW_BOARD}
												onChange={() => onSetViewMode(VIEW_MODE.REVIEW_BOARD)}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												Review Board
											</span>
										</label>
										<label className="inline-flex cursor-pointer items-center rounded-md px-1.5 py-1 hover:bg-[var(--color-bg-secondary)] transition-colors">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4 cursor-pointer"
												name="viewMode"
												value={VIEW_MODE.REPO}
												checked={viewMode === VIEW_MODE.REPO}
												onChange={() => onSetViewMode(VIEW_MODE.REPO)}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												By Repository
											</span>
										</label>
										<label className="inline-flex cursor-pointer items-center rounded-md px-1.5 py-1 hover:bg-[var(--color-bg-secondary)] transition-colors">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4 cursor-pointer"
												name="viewMode"
												value={VIEW_MODE.COMMENT_ATTENTION}
												checked={viewMode === VIEW_MODE.COMMENT_ATTENTION}
												onChange={() =>
													onSetViewMode(VIEW_MODE.COMMENT_ATTENTION)
												}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												Needs Response
											</span>
										</label>
									</div>
								</div>

								{viewMode === VIEW_MODE.SWIMLANE && (
									<div className="ml-4 mt-3 pt-3 space-y-2 border-t border-[var(--color-border)]">
										<div className="flex items-center justify-between">
											<label
												htmlFor="swimlaneWorkstreamsEnabled"
												className="text-sm text-[var(--color-text-secondary)]"
											>
												Enable work streams
											</label>
											<div className="relative inline-block w-12 align-middle select-none">
												<input
													type="checkbox"
													id="swimlaneWorkstreamsEnabled"
													checked={swimlaneWorkstreamsEnabled}
													onChange={() =>
														onToggleSwimlaneWorkstreams(
															!swimlaneWorkstreamsEnabled,
														)
													}
													className="sr-only"
													aria-checked={swimlaneWorkstreamsEnabled}
												/>
												<div
													className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
														swimlaneWorkstreamsEnabled
															? "bg-[var(--color-accent)]"
															: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
													}`}
													onClick={() =>
														onToggleSwimlaneWorkstreams(
															!swimlaneWorkstreamsEnabled,
														)
													}
													role="presentation"
												/>
												<div
													className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
														swimlaneWorkstreamsEnabled
															? "translate-x-5"
															: "translate-x-0"
													} shadow-sm`}
													onClick={() =>
														onToggleSwimlaneWorkstreams(
															!swimlaneWorkstreamsEnabled,
														)
													}
													role="presentation"
												/>
											</div>
										</div>

										<div className="flex items-center justify-between">
											<label
												htmlFor="showSwimlaneMergedColumn"
												className="text-sm text-[var(--color-text-secondary)]"
											>
												Show merged column
											</label>
											<div className="relative inline-block w-12 align-middle select-none">
												<input
													type="checkbox"
													id="showSwimlaneMergedColumn"
													checked={showSwimlaneMergedColumn}
													onChange={() =>
														onToggleShowSwimlaneMergedColumn(
															!showSwimlaneMergedColumn,
														)
													}
													className="sr-only"
													aria-checked={showSwimlaneMergedColumn}
												/>
												<div
													className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
														showSwimlaneMergedColumn
															? "bg-[var(--color-accent)]"
															: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
													}`}
													onClick={() =>
														onToggleShowSwimlaneMergedColumn(
															!showSwimlaneMergedColumn,
														)
													}
													role="presentation"
												/>
												<div
													className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
														showSwimlaneMergedColumn
															? "translate-x-5"
															: "translate-x-0"
													} shadow-sm`}
													onClick={() =>
														onToggleShowSwimlaneMergedColumn(
															!showSwimlaneMergedColumn,
														)
													}
													role="presentation"
												/>
											</div>
										</div>

										<div className="flex items-center justify-between">
											<label
												htmlFor="showSwimlaneDeletedColumn"
												className="text-sm text-[var(--color-text-secondary)]"
											>
												Show deleted column
											</label>
											<div className="relative inline-block w-12 align-middle select-none">
												<input
													type="checkbox"
													id="showSwimlaneDeletedColumn"
													checked={showSwimlaneDeletedColumn}
													onChange={() =>
														onToggleShowSwimlaneDeletedColumn(
															!showSwimlaneDeletedColumn,
														)
													}
													className="sr-only"
													aria-checked={showSwimlaneDeletedColumn}
												/>
												<div
													className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
														showSwimlaneDeletedColumn
															? "bg-[var(--color-accent)]"
															: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
													}`}
													onClick={() =>
														onToggleShowSwimlaneDeletedColumn(
															!showSwimlaneDeletedColumn,
														)
													}
													role="presentation"
												/>
												<div
													className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
														showSwimlaneDeletedColumn
															? "translate-x-5"
															: "translate-x-0"
													} shadow-sm`}
													onClick={() =>
														onToggleShowSwimlaneDeletedColumn(
															!showSwimlaneDeletedColumn,
														)
													}
													role="presentation"
												/>
											</div>
										</div>

										<div className="mt-4 space-y-3 pt-3 border-t border-[var(--color-border)]">
											<div className="text-xs text-[var(--color-text-secondary)] mb-2">
												Auto-hide MRs when <strong>both</strong> conditions are
												met:
											</div>
											<div>
												<label
													htmlFor="swimlaneHideCreatedDaysThreshold"
													className="text-sm text-[var(--color-text-secondary)] block mb-1"
												>
													Created more than (days ago)
												</label>
												<input
													type="number"
													id="swimlaneHideCreatedDaysThreshold"
													min="1"
													value={swimlaneHideCreatedDaysThreshold}
													onChange={(e) =>
														setSwimlaneHideCreatedDaysThreshold(
															Number.parseInt(e.target.value) || 30,
														)
													}
													className="w-full px-3 py-2 text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
												/>
											</div>
											<div>
												<label
													htmlFor="swimlaneHideUpdatedDaysThreshold"
													className="text-sm text-[var(--color-text-secondary)] block mb-1"
												>
													Not updated in (days)
												</label>
												<input
													type="number"
													id="swimlaneHideUpdatedDaysThreshold"
													min="1"
													value={swimlaneHideUpdatedDaysThreshold}
													onChange={(e) =>
														setSwimlaneHideUpdatedDaysThreshold(
															Number.parseInt(e.target.value) || 14,
														)
													}
													className="w-full px-3 py-2 text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
												/>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</CollapsibleSection>

					<CollapsibleSection
						title="Behavior"
						icon={Settings}
						iconColor="text-green-500"
						storageKey="settings-behavior"
					>
						<div className="space-y-4">
							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="useCodiffLinks"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Open merge requests in Codiff
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="useCodiffLinks"
										checked={useCodiffLinks}
										onChange={() => setUseCodiffLinks(!useCodiffLinks)}
										className="sr-only peer"
										aria-checked={useCodiffLinks}
									/>
									{/* The input is sr-only, so the track carries the focus ring. */}
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent)] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--color-bg-primary)] ${
											useCodiffLinks
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() => setUseCodiffLinks(!useCodiffLinks)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											useCodiffLinks ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => setUseCodiffLinks(!useCodiffLinks)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showPendingWithProjectRules"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show pending merge requests with project rule blockers
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showPendingWithProjectRules"
										checked={showPendingWithProjectRules}
										onChange={() =>
											onTogglePendingWithProjectRules(
												!showPendingWithProjectRules,
											)
										}
										className="sr-only"
										aria-checked={showPendingWithProjectRules}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											showPendingWithProjectRules
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() =>
											onTogglePendingWithProjectRules(
												!showPendingWithProjectRules,
											)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											showPendingWithProjectRules
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onTogglePendingWithProjectRules(
												!showPendingWithProjectRules,
											)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showPendingWithAllApprovals"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show pending merge requests with all approvals
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showPendingWithAllApprovals"
										checked={showPendingWithAllApprovals}
										onChange={() =>
											onTogglePendingWithAllApprovals(
												!showPendingWithAllApprovals,
											)
										}
										className="sr-only"
										aria-checked={showPendingWithAllApprovals}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											showPendingWithAllApprovals
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() =>
											onTogglePendingWithAllApprovals(
												!showPendingWithAllApprovals,
											)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											showPendingWithAllApprovals
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onTogglePendingWithAllApprovals(
												!showPendingWithAllApprovals,
											)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex flex-col space-y-3">
								<label
									htmlFor="watchProjects"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show all MRs in these projects
								</label>
								<div className="flex space-x-6 mt-1">
									<ReactTags
										tags={watchProjects}
										handleAddition={(tag) =>
											setWatchProjects((prev) => [...prev, tag])
										}
										handleDelete={(idx) =>
											setWatchProjects(
												watchProjects.filter((_, i) => i !== idx),
											)
										}
										name="watchProjects"
										id="watchProjects"
										allowDragDrop={false}
										placeholder="e.g. cloudflare/fl/fl2"
										inputFieldPosition="bottom"
										maxTags={50}
										// TODO: suggestions/handleFilterSuggestions
									/>
								</div>
							</div>

							<div className="flex flex-col space-y-3">
								<label
									htmlFor="excludeProjects"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Exclude MRs in these projects
								</label>
								<div className="flex space-x-6 mt-1">
									<ReactTags
										tags={excludeProjects}
										handleAddition={(tag) =>
											setExcludeProjects((prev) => [...prev, tag])
										}
										handleDelete={(idx) =>
											setExcludeProjects(
												excludeProjects.filter((_, i) => i !== idx),
											)
										}
										name="excludeProjects"
										id="excludeProjects"
										allowDragDrop={false}
										placeholder="e.g. cloudflare/fl/fl2"
										inputFieldPosition="bottom"
										maxTags={50}
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="enableShortcut"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Enable keyboard shortcut
									<span className="ml-2 text-xs bg-[var(--color-bg-secondary)] px-2 py-1 rounded text-[var(--color-text-secondary)]">
										⌘F for search
									</span>
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="enableShortcut"
										checked={enableShortcut}
										onChange={() => setenableShortcut(!enableShortcut)}
										className="sr-only"
										aria-checked={enableShortcut}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors cursor-pointer ${
											enableShortcut
												? "bg-[var(--color-accent)]"
												: "bg-gray-300 dark:bg-gray-600 monokai:bg-monokai-comment"
										}`}
										onClick={() => setenableShortcut(!enableShortcut)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform cursor-pointer ${
											enableShortcut ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => setenableShortcut(!enableShortcut)}
										role="presentation"
									/>
								</div>
							</div>
						</div>
					</CollapsibleSection>

					<CollapsibleSection
						title="Sorting"
						icon={SortDesc}
						iconColor="text-amber-500"
						storageKey="settings-sorting"
					>
						<div className="flex flex-col space-y-3">
							<div
								className="text-sm font-medium text-[var(--color-text-primary)]"
								id="sort-group-label"
							>
								Sort open merge requests by:
							</div>
							<div
								className="flex space-x-6 mt-1"
								role="radiogroup"
								aria-labelledby="sort-group-label"
							>
								<label className="inline-flex items-center">
									<input
										type="radio"
										className="form-radio text-[var(--color-accent)] h-4 w-4"
										name="sortOption"
										value="updatedAt"
										checked={sortOption === "updatedAt"}
										onChange={() => onSetSortOption("updatedAt")}
									/>
									<span className="ml-2 text-sm text-[var(--color-text-primary)]">
										Last updated
									</span>
								</label>
								<label className="inline-flex items-center">
									<input
										type="radio"
										className="form-radio text-[var(--color-accent)] h-4 w-4"
										name="sortOption"
										value="createdAt"
										checked={sortOption === "createdAt"}
										onChange={() => onSetSortOption("createdAt")}
									/>
									<span className="ml-2 text-sm text-[var(--color-text-primary)]">
										Creation time
									</span>
								</label>
							</div>
						</div>
					</CollapsibleSection>

					<CollapsibleSection
						title="Theme"
						icon={Palette}
						iconColor="text-purple-500"
						storageKey="settings-theme"
						headerActions={
							<div className="flex items-center gap-2 text-xs font-medium text-[var(--color-text-secondary)]">
								<span>System</span>
								<button
									type="button"
									onClick={toggleSystemThemeControls}
									className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
										enableSystemThemeControls
											? "bg-[var(--color-accent)]"
											: "bg-[var(--color-border)]"
									}`}
									aria-label="Toggle system theme controls"
									aria-pressed={enableSystemThemeControls}
								>
									<span
										className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
											enableSystemThemeControls
												? "translate-x-4"
												: "translate-x-0.5"
										}`}
									/>
								</button>
							</div>
						}
					>
						<div className="flex flex-col space-y-3">
							<div
								className="text-sm font-medium text-[var(--color-text-primary)]"
								id="theme-group-label"
							>
								{enableSystemThemeControls
									? "Choose theme behavior:"
									: "Choose theme:"}
							</div>
							<ThemeSelector
								enableSystemThemeControls={enableSystemThemeControls}
							/>
						</div>
					</CollapsibleSection>
				</div>

				<div className="mt-8 flex justify-end">
					<button
						type="button"
						onClick={onClose}
						className="cursor-pointer px-5 py-2 bg-[var(--color-accent)] text-[var(--color-accent-contrast)] rounded-lg text-sm font-medium hover:opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-2"
					>
						Close
					</button>
				</div>
			</div>
		</div>
	);
};
